import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AppLayout } from './components/layout/AppLayout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useData } from './hooks/useData';
import { dataManager } from './utils/dataManager';
import { LockScreen } from './components/auth/LockScreen';
import { useBrandingEffect } from './hooks/useBrandingEffect';
import { useStatsMigrationEffect } from './hooks/useStatsMigrationEffect';
import { useThemeEffect } from './hooks/useThemeEffect';
import { useAuth } from './hooks/useAuth';
import { PageLoader } from './components/layout/PageLoader';

// Lazy Import Views
const DashboardView = React.lazy(() => import('./pages/Dashboard/DashboardView'));
const MatchPage = React.lazy(() => import('./pages/Match/MatchPage'));
const StandingsView = React.lazy(() => import('./pages/Standings/StandingsView'));
const PastResultsView = React.lazy(() => import('./pages/Results/PastResultsView').then(module => ({ default: module.PastResultsView })));
const AdminView = React.lazy(() => import('./pages/Admin/AdminView'));
const PressRoomPage = React.lazy(() => import('./pages/Press/PressRoom'));
const TVView = React.lazy(() => import('./pages/TV/TVView'));
const RecordsView = React.lazy(() => import('./pages/Records/RecordsView'));
const RulesView = React.lazy(() => import('./pages/Rules/RulesView').then(module => ({ default: module.RulesView })));

function App() {
  const gameMode = useData(() => dataManager.getGameMode());
  const appLogo = useData(() => dataManager.getAppLogo());
  const isInitialSyncComplete = useData(() => dataManager.getIsInitialSyncComplete());

  // Initialize App Effects
  useThemeEffect();
  useBrandingEffect(appLogo);
  useStatsMigrationEffect(gameMode);

  // Auth State
  const { isAuthenticated, loading, loginWithAppPassword } = useAuth();

  return (
    <ErrorBoundary>
      {loading ? (
        <PageLoader />
      ) : !isAuthenticated ? (
        <LockScreen onUnlock={loginWithAppPassword} />
      ) : !isInitialSyncComplete ? (
        <PageLoader />
      ) : (
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route element={<AppLayout />}>
              <Route path="/" element={<DashboardView />} />
              <Route path="/match" element={<MatchPage />} />
              <Route path="/rankings" element={<StandingsView />} />
              <Route path="/records" element={<RecordsView />} />
              <Route path="/results" element={<PastResultsView />} />
              <Route path="/press" element={<PressRoomPage />} />
              <Route path="/admin" element={<AdminView />} />
              <Route path="/tv" element={<TVView />} />
              <Route path="/rules" element={<RulesView />} />
              {/* Catch-all redirect to Dashboard to prevent stray routes or stuck states */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </Suspense>
      )}
    </ErrorBoundary>
  );
}

export default App;
